import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Nullable,
  floatVal,
} from '@jamesgmarks/utilities';
import { roundFloatToCents } from '@hydra/internal';
import { isValidCurrencyCode, TCurrencyCode } from '@llws/hydra-shared';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { Charge } from '../../../../../../entities/hydra';
import { IClientDropdownItem } from '../../../../entity-interfaces/IClientDropdownItem';
import { INoteComment } from '../../../../../../entities/jsonTypes/INoteComment';
import {
  createBulkSubscriptionCredits,
  createCreditNotesFromSplits,
} from '../../../../redux/features/credit-notes/actions';
import { useAppSelector } from '../../../../redux/hooks';
import { ICredit } from '../../../../interfaces/ICredit';
import {
  BulkSubscriptionCreditModalProps,
  BulkCreditModalMode,
  SelectedCredit,
  ICreditNoteSplit,
  SubscriptionSelectionMode,
} from '.';
import {
  ClientSelectionMode,
  ChargeSelectionMode,
  ReviewMode,
  SplitsReviewMode,
  CreatedNotesMode,
  getDialogTitle,
} from '.';

// Local currency code assertion function to avoid dependency issues
// TODO this needs to move to hydra shared
const assertTCurrencyCode = (currency: string | undefined | null): TCurrencyCode => {
  const currencyStr = currency ?? '';
  if (!isValidCurrencyCode(currencyStr)) {
    throw new Error(`Invalid currency code: ${currencyStr}`);
  }
  return currencyStr as TCurrencyCode;
};

export const BulkSubscriptionCreditModal = ({
  open,
  onClose,
}: BulkSubscriptionCreditModalProps) => {
  // Modal state
  const [mode, setMode] = useState<BulkCreditModalMode>('setup');
  const [selectedClient, setSelectedClient] = useState<Nullable<IClientDropdownItem>>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Nullable<ISubscriptions>>(null);
  const [selectedCredits, setSelectedCredits] = useState<SelectedCredit[]>([]);
  const [selectedChargeRowIndex, setSelectedChargeRowIndex] = useState<Nullable<number>>(null);
  const [selectedChargeInvoiceIds, setSelectedChargeInvoiceIds] = useState<number[]>([]);
  const [creditNoteSplits, setCreditNoteSplits] = useState<ICreditNoteSplit[]>([]);
  const [creditNoteComment, setCreditNoteComment] = useState('');
  const [displayCreditNoteCommentOnPdf, setDisplayCreditNoteCommentOnPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redux state
  const charges = useAppSelector((state) => state.lookups.charges);

  const handleClose = () => {
    setMode('setup');
    setSelectedClient(null);
    setSelectedSubscription(null);
    setSelectedCredits([]);
    setSelectedChargeRowIndex(null);
    setSelectedChargeInvoiceIds([]);
    setCreditNoteSplits([]);
    setCreditNoteComment('');
    setDisplayCreditNoteCommentOnPdf(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleClientChanged = (client: Nullable<IClientDropdownItem>) => {
    setSelectedClient(client);
  };

  const handleSubscriptionSelected = (subscription: Nullable<ISubscriptions>) => {
    setSelectedSubscription(subscription);
    if (subscription) {
      setMode('charge-selection');
    }
  };

  const addChargeToSelection = (charge: Charge, invoiceIds: number[]) => {
    if (!selectedSubscription) return;

    // Filter out invoices that already have credits selected
    const availableInvoiceIds = invoiceIds.filter(invoiceId => {
      // Check if credit for this charge and invoice is already selected
      return !selectedCredits.some(sc => (
        sc.credit.generatedSubscriptionId === charge.id &&
        sc.credit.invoiceId === invoiceId &&
        sc.subscription.id === selectedSubscription.id
      ));
    });

    // Create credit objects for each available invoice
    const newCredits = availableInvoiceIds.map(invoiceId => {
      // Find the tax information for this specific invoice
      const chargeHasTax = charge.chargeHasTaxes?.find(cht =>
        cht.invoiceId === invoiceId);

      // Use invoice-specific amounts if available
      const invoiceSubtotal = chargeHasTax?.subtotal ? floatVal(chargeHasTax.subtotal) : 0;
      const invoiceTaxAmount = chargeHasTax?.taxAmount ? floatVal(chargeHasTax.taxAmount) : 0;

      const taxRate = chargeHasTax?.taxRate || '0.00';
      const invoiceTaxName = chargeHasTax?.taxName || null;

      const currencyCode = assertTCurrencyCode(chargeHasTax?.currency);

      const credit: ICredit = {
        creditNumber: null,
        freshbooksClientId: selectedClient?.billingAccountId ?? 0,
        clientId: selectedClient?.id ?? null,
        partnerId: null,
        partnerClawback: null,
        clawbackFromPartner: null,
        invoiceId: invoiceId,
        invoiceNumber: null,
        subscriptionId: selectedSubscription.id ?? null,
        generatedSubscriptionId: charge.id,
        freshbooksCreditId: null,
        currencyCode: currencyCode,
        amount: roundFloatToCents(invoiceSubtotal).toString(),
        taxRate: taxRate,
        taxAmount: roundFloatToCents(invoiceTaxAmount).toString(),
        taxName: invoiceTaxName,
        description: `Credit for ${selectedSubscription.invoiceDescription}`,
        hydraState: 'active',
        creditDate: new Date(),
        year: charge.year,
        month: charge.month,
      };

      return {
        credit,
        subscription: selectedSubscription,
        originalAmount: invoiceSubtotal,
      };
    });

    // Add all new credits at once
    if (newCredits.length > 0) {
      setSelectedCredits(prev => [...prev, ...newCredits]);
    }

    // Reset the current selection UI
    setSelectedChargeRowIndex(null);
    setSelectedChargeInvoiceIds([]);
  };

  const removeChargeFromSelection = (chargeId: number, subscriptionId: number) => {
    setSelectedCredits(prev => prev.filter(sc => (
      !(sc.credit.generatedSubscriptionId === chargeId && sc.subscription.id === subscriptionId)
    )));
  };

  const updateCreditAmount = (creditIndex: number, newAmount: string) => {
    // Validate input format: allow empty string or valid decimal numbers (e.g., "10", "10.5", "10.50")
    if (newAmount === '' || /^\d*\.?\d{0,2}$/.test(newAmount)) {
      const currentCredit = selectedCredits[creditIndex];

      if (!currentCredit) {
        return;
      }

      const parsedPreTaxAmount = floatVal(newAmount);
      const userEnteredPreTaxAmount = Number.isFinite(parsedPreTaxAmount) ? parsedPreTaxAmount : 0;
      const currentTaxRate = floatVal(currentCredit.credit.taxRate) || 0;

      // Calculate the maximum allowed pre-tax amount based on the original total
      const maxAllowedPreTaxAmount = currentCredit.originalAmount;

      // Prevent amounts exceeding the original
      if (userEnteredPreTaxAmount > maxAllowedPreTaxAmount) {
        return;
      }

      // Calculate new tax amount based on user input
      const calculatedTaxAmount = userEnteredPreTaxAmount * currentTaxRate;

      // Update the credit in the state
      setSelectedCredits(prev => prev.map((sc, index) => {
        if (index === creditIndex) {
          return {
            ...sc,
            credit: {
              ...sc.credit,
              amount: newAmount,
              taxAmount: roundFloatToCents(calculatedTaxAmount).toString(),
            },
          };
        }
        return sc;
      }));
    }
  };

  const updateCreditDescription = (creditIndex: number, newDescription: string) => {
    setSelectedCredits(prev => prev.map((sc, index) => {
      if (index === creditIndex) {
        return {
          ...sc,
          credit: {
            ...sc.credit,
            description: newDescription,
          },
        };
      }
      return sc;
    }));
  };

  const removeCreditFromReview = (creditIndex: number) => {
    setSelectedCredits(prev => prev.filter((_, index) => index !== creditIndex));
  };

  /**
   * Builds the `INoteComment[]` array to attach to each credit note being created.
   *
   * Returns `null` when the user has not entered any comment text, so that the
   * `notes` field is omitted from the credit note payload entirely rather than
   * being set to an empty array.
   */
  const buildCreditNoteComments = (): Nullable<INoteComment[]> => {
    const trimmedComment = creditNoteComment.trim();
    if (!trimmedComment) {
      return null;
    }

    return [{
      note: trimmedComment,
      version: '2.0',
      displayCommentOnPdf: displayCreditNoteCommentOnPdf,
    }];
  };

  /**
   * Returns a copy of `splits` with the current user-entered comment applied to every
   * split via {@link buildCreditNoteComments}. Called just before the splits are passed
   * to the API so that the latest comment state is always included.
   */
  const addCommentsToSplits = (splits: ICreditNoteSplit[]): ICreditNoteSplit[] => (
    splits.map((split) => ({
      ...split,
      notes: buildCreditNoteComments(),
    }))
  );

  const handleCreateCredits = async () => {
    // Validate that all credits have valid amounts
    const invalidCredits = selectedCredits.filter(sc =>
      !sc.credit.amount || sc.credit.amount === '' || floatVal(sc.credit.amount) <= 0);

    if (invalidCredits.length > 0) {
      alert(`Please ensure all credits have valid amounts greater than 0. ` +
        `Found ${invalidCredits.length} invalid amount(s).`);
      return;
    }

    setIsSubmitting(true);

    try {
      const data: {
        clientId?: number;
        subscriptionIds?: number[];
        credits?: ICredit[];
      } = {};

      // Use selected credits data
      if (selectedCredits.length > 0) {
        data.credits = selectedCredits.map(sc => sc.credit);
      } else if (selectedClient) {
        data.clientId = selectedClient.id;
      }

      // Get splits for review instead of creating credit notes immediately
      const splits = await createBulkSubscriptionCredits(data);
      if (!splits || !Array.isArray(splits)) {
        throw new Error(`Invalid splits response: Expected array, received ${typeof splits}`);
      }

      setCreditNoteSplits(addCommentsToSplits(splits));
      setMode('splits-review');
    } catch (error) {
      console.error('Error getting credit note splits:', error);
      setCreditNoteSplits([]);
      // Optionally show user-friendly error message
      alert('Failed to create credit note splits. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSplits = async () => {
    setIsSubmitting(true);

    try {
      await createCreditNotesFromSplits(addCommentsToSplits(creditNoteSplits));
      setMode('created-notes');
    } catch (error) {
      console.error('Error creating credit notes from splits:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    if (mode === 'setup') {
      return selectedClient !== null;
    }
    return false;
  };

  const handleProceedFromSetup = () => {
    if (selectedClient) {
      setMode('client-selection');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {getDialogTitle(mode)}
      </DialogTitle>
      <DialogContent>
        {mode === 'setup' && (
          <ClientSelectionMode
            selectedClient={selectedClient}
            onClientChanged={handleClientChanged}
          />
        )}
        {mode === 'client-selection' && (
          <SubscriptionSelectionMode
            selectedClient={selectedClient}
            selectedCredits={selectedCredits}
            onSubscriptionSelected={handleSubscriptionSelected}
          />
        )}
        {mode === 'charge-selection' && (
          <ChargeSelectionMode
            selectedSubscription={selectedSubscription}
            charges={charges}
            selectedCredits={selectedCredits}
            selectedChargeRowIndex={selectedChargeRowIndex}
            selectedChargeInvoiceIds={selectedChargeInvoiceIds}
            onBackToClients={() => setMode('client-selection')}
            onChargeAdded={addChargeToSelection}
            onChargeRemoved={removeChargeFromSelection}
            onChargeRowSelected={setSelectedChargeRowIndex}
            setSelectedChargeInvoiceIds={setSelectedChargeInvoiceIds}
            onNextToReview={() => setMode('review')}
          />
        )}
        {mode === 'review' && (
          <ReviewMode
            selectedCredits={selectedCredits}
            isSubmitting={isSubmitting}
            creditNoteComment={creditNoteComment}
            displayCreditNoteCommentOnPdf={displayCreditNoteCommentOnPdf}
            onCreditAmountUpdate={updateCreditAmount}
            onCreditDescriptionUpdate={updateCreditDescription}
            onCreditRemove={removeCreditFromReview}
            onCreditNoteCommentChange={setCreditNoteComment}
            onDisplayCreditNoteCommentOnPdfChange={setDisplayCreditNoteCommentOnPdf}
            onCreateCredits={handleCreateCredits}
          />
        )}
        {mode === 'splits-review' && (
          <SplitsReviewMode
            creditNoteSplits={creditNoteSplits}
            selectedClient={selectedClient}
            isSubmitting={isSubmitting}
            onBackToReview={() => setMode('review')}
            onConfirmSplits={handleConfirmSplits}
          />
        )}
        {mode === 'created-notes' && (
          <CreatedNotesMode
            selectedClient={selectedClient}
            createdNotesCount={creditNoteSplits.length}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {mode === 'created-notes' ? 'OK!' : 'Cancel'}
        </Button>
        {mode === 'setup' && (
          <Button
            onClick={handleProceedFromSetup}
            variant="contained"
            disabled={!canProceed() || isSubmitting}
          >
            Select Subscriptions
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
